import React from 'react';
import { createUseStyles } from 'react-jss';
import { useT } from '../../i18n';
import useModalEscape from '../../hooks/useModalEscape';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

/**
 * The shell both menu panels sit in — the room list and the create form.
 *
 * It exists so the two are not two copies of the same chrome. The panels
 * themselves stay pure content components (`RoomList`, `CreateRoomForm`), which
 * is also what keeps their tests free of modal plumbing.
 *
 * ESC goes through the shared `useModalEscape` stack rather than a listener of
 * its own, so these behave like every other modal in the app: one Escape peels
 * off one layer.
 */
const useStyles = createUseStyles({
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    background: 'rgba(14,8,4,0.72)',
  },
  panel: {
    width: '100%',
    maxWidth: '420px',
    maxHeight: '86dvh',
    fallbacks: [{ maxHeight: '86vh' }],
    boxSizing: 'border-box',
    background: '#2a1d13',
    border: '1px solid #6b4622',
    borderRadius: '14px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    boxShadow: [
      'inset 0 1px 0 rgba(255,255,255,0.12)',
      'inset 0 -3px 0 rgba(0,0,0,0.4)',
      '0 24px 60px rgba(0,0,0,0.6)',
    ].join(','),
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
  title: {
    margin: 0,
    color: '#f5e6c8',
    fontSize: '17px',
    fontWeight: 700,
    letterSpacing: '0.5px',
  },
  close: {
    width: '34px',
    height: '34px',
    flexShrink: 0,
    background: 'none',
    border: '1px solid #4a3020',
    borderRadius: '8px',
    color: '#c4a070',
    cursor: 'pointer',
    fontFamily: MONO,
    fontSize: '13px',
    lineHeight: 1,
    '&:hover': { borderColor: '#d94f2b', color: '#fff3e6' },
    '&:focus-visible': { outline: '2px solid #e35c36', outlineOffset: '2px' },
  },
});

function MenuModal({ open, title, onClose, children }) {
  const classes = useStyles();
  const t = useT();
  useModalEscape(open, onClose);

  if (!open) return null;

  // Only a press that both starts and ends on the backdrop closes it — a drag
  // begun on a room row and released outside is not a backdrop click.
  const onBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className={classes.overlay}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onBackdrop}
    >
      <div className={classes.panel}>
        <div className={classes.head}>
          <h2 className={classes.title}>{title}</h2>
          <button
            type="button"
            className={classes.close}
            onClick={onClose}
            aria-label={t('a11y.close')}
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default MenuModal;
